// src/components/TalentPoolPage.js
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import '../css/SquadTrackerPage.css';
import TalentProfileModal from './TalentProfileModal';
import GamifiedConfirmModal from './GamifiedConfirmModal';

// ✅ Live API
import { TalentsAPI } from '../data/talent';
// ✅ Shared shortlist store
import { addToShortlist, isShortlisted, removeFromShortlist, getAllShortlistedTalentIds, getShortlist } from '../data/shortlist';
// ✅ Credits
import { useCredits } from './CreditsContext';
// ✅ Auth
import { useAuth } from '../contexts/AuthContext';

function TalentPool() {
  const location = useLocation();
  const qp = new URLSearchParams(location.search);
  const presetRole =
    (location.state?.presetFilters?.role || qp.get('role') || '').trim();

  // auth
  const { currentUser } = useAuth();
  
  
  // credits
  const {
    screeningCredits,
    refundScreening,
    consumeShortlistPass,
  } = useCredits();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [sortBy, setSortBy] = useState('alias');
  const [sortOrder, setSortOrder] = useState('asc');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTalent, setModalTalent] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelRow, setCancelRow] = useState(null);

  // data state
  const [rowsRaw, setRowsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const debounce = useRef();

  // Hire/save states
  const [busyId, setBusyId] = useState(null);
  const [saveErr, setSaveErr] = useState('');

  // Shortlist state - show all shortlisted talents to all users
  const [allShortlistedIds, setAllShortlistedIds] = useState([]);
  const [shortlistTick, setShortlistTick] = useState(0);

  // User verification state
  const [userData, setUserData] = useState({
    isVerified: false,
    isLoading: true
  });

  // Verification modal state
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);

  // Fetch user verification status
  const fetchUserData = async () => {
    if (!currentUser?.uid) {
      setUserData({ isVerified: false, isLoading: false });
      return;
    }

    try {
      setUserData(prev => ({ ...prev, isLoading: true }));
      
      const response = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user?userId=${currentUser.uid}`);
      
      if (response.ok) {
        const result = await response.json();
        const user = result.data;
        
        setUserData({
          isVerified: user?.is_verified || user?.email_verified || false,
          isLoading: false
        });
      } else {
        setUserData({ isVerified: false, isLoading: false });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserData({ isVerified: false, isLoading: false });
    }
  };

  useEffect(() => {
    if (currentUser?.uid) {
      fetchUserData();
    }
  }, [currentUser?.uid]);
  
  // Load all shortlisted talent IDs (for showing shortlisted status to all users)
  useEffect(() => {
    const loadAllShortlisted = async () => {
      try {
        const ids = await getAllShortlistedTalentIds();
        setAllShortlistedIds(ids);
      } catch (error) {
        console.error('Failed to load shortlisted talent IDs:', error);
      }
    };
    
    loadAllShortlisted();
    
    // Re-render when shortlist changes
    const bump = () => {
      setShortlistTick((x) => x + 1);
      loadAllShortlisted(); // Reload shortlisted IDs
    };
    window.addEventListener('exodia:shortlist-changed', bump);
    window.addEventListener('storage', bump);
    return () => {
      window.removeEventListener('exodia:shortlist-changed', bump);
      window.removeEventListener('storage', bump);
    };
  }, []);

  const mkEmail = (name = '') => {
    const simple = name
      .toLowerCase()
      .replace(/[^a-z]+/g, '.')
      .replace(/\.+/g, '.')
      .replace(/^\.|\.$/g, '');
    return `${simple || 'talent'}@exodia.com`;
  };

  const roleCategory = (role = '') => {
    const r = (role || '').toLowerCase();
    if (/designer|artist|animator|ui\/?ux/.test(r)) return 'Creative';
    if (/engineer|developer|programmer|devops|data|unity|unreal|technical/.test(r)) return 'Technical';
    if (/manager|producer|project/.test(r)) return 'Management';
    return 'Other';
  };

  const load = async (q = '') => {
    try {
      setLoading(true);
      setErr('');
      const list = await TalentsAPI.list({ q });
      setRowsRaw(Array.isArray(list) ? list : []);
    } catch (e) {
      setErr(e?.message || String(e));
      setRowsRaw([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(''); }, []);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(searchTerm.trim()), 300);
    return () => clearTimeout(debounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (presetRole) setSelectedRole(presetRole);
  }, [presetRole, location.key]);



  const rows = useMemo(() => {
    const src = Array.isArray(rowsRaw) ? rowsRaw : [];
    return src.map((t, idx) => {
      const id = String(t.talentId ?? t.id ?? t.ROWID ?? `t_${idx}`);
      const name = t.name || '—';
      const role = t.role || '—';
      const availability = t.availability || t.workPreferences?.availability || '—';
      const seniority = t.seniority || 'Mid';

      return {
        id,
        alias: t.alias || name || '—',
        name,
        role,
        category: roleCategory(role),
        availability,
        seniority,
        email: mkEmail(name),
        shortlisted: allShortlistedIds.includes(id), // Check if talent is shortlisted by anyone
        __raw: t,
      };
    });
    
    // Sort: shortlisted talents go to the bottom
    return rows.sort((a, b) => {
      if (a.shortlisted && !b.shortlisted) return 1; // a goes after b
      if (!a.shortlisted && b.shortlisted) return -1; // a goes before b
      return 0; // maintain original order for same shortlist status
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsRaw, shortlistTick, allShortlistedIds]); // shortlistTick and allShortlistedIds trigger re-computation

  const roleOptions = useMemo(() => {
    const uniq = new Set(rows.map((r) => r.role));
    if (presetRole) uniq.add(presetRole);
    const list = Array.from(uniq).filter(Boolean).sort((a, b) => a.localeCompare(b));
    return ['all', ...list];
  }, [rows, presetRole]);

  const totals = useMemo(
    () => ({
      all: rows.length,
      creative: rows.filter((r) => r.category === 'Creative').length,
      technical: rows.filter((r) => r.category === 'Technical').length,
      management: rows.filter((r) => r.category === 'Management').length,
    }),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = rows.filter((r) => {
      const matchesSearch =
        !q ||
        r.alias.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        r.role.toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q);
      const matchesRole = selectedRole === 'all' || r.role === selectedRole;
      return matchesSearch && matchesRole;
    });

    list.sort((a, b) => {
      if (sortBy === 'id') {
        const an = Number(a.id), bn = Number(b.id);
        if (Number.isFinite(an) && Number.isFinite(bn))
          return sortOrder === 'asc' ? an - bn : bn - an;
      }
      let av = String(a[sortBy] ?? '').toLowerCase();
      let bv = String(b[sortBy] ?? '').toLowerCase();
      if (av === bv) return 0;
      return sortOrder === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

    return list;
  }, [rows, searchTerm, selectedRole, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const openTalentModal = (t) => {
    setModalTalent(t.__raw);
    setModalOpen(true);
  };
  const closeTalentModal = () => {
    setModalOpen(false);
    setModalTalent(null);
  };

  // ✅ Hire with credits
  const hireTalent = async (t) => {
    performHireTalent(t);
  };

  const performHireTalent = async (t) => {
    setSaveErr('');
    const raw = t.__raw || {};
    const id = String(raw.talentId ?? raw.id ?? t.id);

    // No credit check needed for shortlisting

    // Block if not authenticated
    if (!currentUser?.uid) {
      alert('Please log in to shortlist talents');
      return;
    }

    // Check shortlist pass availability
    try {
      // The backend will check shortlist_pass count and deduct it
      // We don't need to check the count on frontend anymore
    } catch (error) {
      console.error('Error checking shortlist:', error);
      // Continue with shortlisting if we can't check
    }

    // Add to shortlist via API
    const ok = await addToShortlist(raw, currentUser.uid);
    if (!ok) {
      alert(`Already in shortlist: ${t.alias}`);
      return;
    }

    // Consume shortlist pass for real-time UI update
    await consumeShortlistPass(1);

    const payload = {
      talentId: String(id || ''),
      name: raw.name || t.alias || '—',
      role: raw.role || t.role || '—',
      stage: 'Shortlist',
      seniority: raw.seniority || t.seniority || 'Mid',
      availability: raw.availability || t.availability || 'Immediate',
    };

    try {
      setBusyId(id);
      if (raw.ROWID) {
        await TalentsAPI.update(raw.ROWID, { stage: 'Shortlist' });
      } else {
        await TalentsAPI.create(payload);
      }
      await load(searchTerm.trim());
    } catch (e) {
      setSaveErr(e?.message || String(e));
      // rollback: remove from shortlist
      await removeFromShortlist(id, currentUser.uid);
    } finally {
      setBusyId(null);
    }
  };

  // ❌ Cancel shortlist + refund credit
  const cancelShortlist = async (t) => {
    const raw = t.__raw || {};
    const id = String(raw.talentId ?? raw.id ?? t.id);

    // Block if not authenticated
    if (!currentUser?.uid) {
      alert('Please log in to manage shortlist');
      return;
    }

    // Remove from shortlist via API
    await removeFromShortlist(id, currentUser.uid);
    // Refund credit
    refundScreening(1);

    try {
      setBusyId(id);
      // Optional: reflect server-side if you track "stage" there
      if (raw.ROWID) {
        await TalentsAPI.update(raw.ROWID, { stage: null }); // or your default stage
      }
      await load(searchTerm.trim());
    } catch (e) {
      setSaveErr(e?.message || String(e));
      // rollback: re-add talent to shortlist
      await addToShortlist(raw, currentUser.uid);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="squad-tracker-page">
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideInUp {
            from { 
              opacity: 0;
              transform: translateY(30px) scale(0.9);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-10px);
            }
            60% {
              transform: translateY(-5px);
            }
          }
        `}
      </style>
      <Header />

      <main className="squad-main">
        {/* Hero */}
        <section className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">Talent Pool</h1>
            <p className="hero-subtitle">Browse vetted talents. Hire in days, not months.</p>
            <p className="hero-description">
              Filter by role, view profiles, and shortlist candidates you like.
            </p>
            <br />
            <div className="hero-cta">
              <span className="results-count" style={{ marginLeft: 12 }}>
                {totals.all} total • {totals.creative} Creative • {totals.technical} Technical • {totals.management} Management
              </span>
              {/* 👇 show remaining credits */}
              <span className="results-count" style={{ marginLeft: 24 }}>
                Screening credits: <strong>{screeningCredits ?? 0}</strong>
              </span>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="filters-section">
          <div className="filters-container">
            <div className="search-box">
              <span className="search-icon">🔎</span>
              <input
                type="text"
                placeholder="Search by name or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-controls">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="filter-select"
              >
                {roleOptions.map((r) => (
                  <option key={r} value={r}>
                    {r === 'all' ? 'All Roles' : r}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="table-section">
          <div className="table-header">
            <h2 className="section-title">Available Talents</h2>
            <span className="results-count">
              {loading ? 'Loading…' : err ? `Error: ${err}` : `${filtered.length} talents found`}
            </span>
          </div>

          <div className="table-container">
            <table className="employees-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('id')} className="sortable">
                    ID {sortBy === 'id' && <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th onClick={() => handleSort('alias')} className="sortable">
                    Talent {sortBy === 'alias' && <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th onClick={() => handleSort('role')} className="sortable">
                    Role {sortBy === 'role' && <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th onClick={() => handleSort('availability')} className="sortable">
                    Availability {sortBy === 'availability' && <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th onClick={() => handleSort('seniority')} className="sortable">
                    Seniority {sortBy === 'seniority' && <span className="sort-indicator">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="employee-row">
                    <td className="employee-name">
                      <div className="name-cell">
                        <span className="name">{t.id}</span>
                      </div>
                    </td>
                    <td className="employee-name">
                      <div className="name-cell">
                        <span className="name">{t.alias}</span>
                      </div>
                    </td>
                    <td className="employee-role">
                      <span className="role">{t.role}</span>
                    </td>
                    <td className="employee-availability">
                      <span className="availability">{t.availability}</span>
                    </td>
                    <td className="employee-seniority">
                      <span className="seniority">{t.seniority}</span>
                    </td>
                    <td className="employee-actions">
                      <div className="action-buttons">
                        {t.shortlisted ? (
                          <>
                            <button
                              className="action-btn view"
                              disabled
                              title="Already shortlisted"
                            >
                              Shortlisted
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="action-btn view" onClick={() => openTalentModal(t)}>
                              View
                            </button>
                            <button
                              className="action-btn hire"
                              onClick={() => hireTalent(t)}
                              disabled={busyId === t.id}
                              title="Add to Shortlist"
                            >
                              {busyId === t.id ? 'Saving…' : 'Add to Shortlist'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

                {!loading && !err && filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '1rem' }}>
                      No talents match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {saveErr && (
            <div style={{ marginTop: 12, color: '#c00', fontSize: 14 }}>
              Save error: {saveErr}
            </div>
          )}
        </section>
      </main>

      <TalentProfileModal isOpen={modalOpen} onClose={closeTalentModal} talent={modalTalent} />
      <GamifiedConfirmModal
        isOpen={cancelOpen}
        onClose={() => { setCancelOpen(false); setCancelRow(null); }}
        onConfirm={() => cancelShortlist(cancelRow)}
        talentName={cancelRow?.alias || cancelRow?.name || 'this talent'}
        creditDelta={+1}
      />

      {/* Verification Prompt Modal */}
      {showVerificationPrompt && (
        <div 
          className="verification-prompt-overlay" 
          onClick={() => setShowVerificationPrompt(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            backdropFilter: 'blur(10px)',
            animation: 'fadeIn 0.4s ease-out'
          }}
        >
          <div 
            className="verification-prompt-modal" 
            onClick={e => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              borderRadius: '24px',
              padding: '40px',
              maxWidth: '600px',
              width: '90%',
              textAlign: 'center',
              color: 'white',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
              border: '2px solid rgba(255, 255, 255, 0.1)',
              animation: 'slideInUp 0.4s ease-out'
            }}
          >
            <div style={{ marginBottom: '30px', position: 'relative' }}>
              <div style={{ 
                fontSize: '64px', 
                marginBottom: '20px',
                animation: 'bounce 2s infinite'
              }}>
                🔒
              </div>
              <h2 style={{ 
                fontSize: '32px', 
                fontWeight: '700', 
                margin: '0 0 15px 0',
                textShadow: '0 3px 6px rgba(0, 0, 0, 0.3)',
                background: 'linear-gradient(135deg, #ffd700, #ff6b6b)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Shortlist Limit Reached
              </h2>
              <p style={{ 
                color: '#b8c5d6', 
                fontSize: '18px', 
                lineHeight: '1.6',
                margin: '0 0 20px 0'
              }}>
                You've reached the limit of 5 shortlisted talents. Please remove some talents from your shortlist before adding new ones.
              </p>
            </div>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '25px',
              marginBottom: '30px',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{ 
                color: '#ffffff', 
                fontSize: '20px', 
                fontWeight: '600', 
                margin: '0 0 20px 0' 
              }}>
                What you can do:
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '15px',
                  padding: '15px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  <div style={{ 
                    fontSize: '32px',
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px'
                  }}>
                    🗑️
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
                    <span style={{ color: '#4ade80', fontSize: '18px', fontWeight: '600' }}>Remove existing talents</span>
                    <span style={{ color: '#b8c5d6', fontSize: '14px', fontWeight: '500' }}>Go to Squad Tracker to remove talents from your shortlist</span>
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '15px',
                  padding: '15px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  <div style={{ 
                    fontSize: '32px',
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px'
                  }}>
                    📋
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
                    <span style={{ color: '#4ade80', fontSize: '18px', fontWeight: '600' }}>Review your shortlist</span>
                    <span style={{ color: '#b8c5d6', fontSize: '14px', fontWeight: '500' }}>Check which talents you've already shortlisted</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowVerificationPrompt(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#b8c5d6',
                  padding: '15px 30px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  minWidth: '150px'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.color = '#ffffff';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = '#b8c5d6';
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowVerificationPrompt(false);
                  // Navigate to Squad Tracker to manage shortlist
                  window.location.href = '#/squad-tracker';
                }}
                style={{
                  background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '15px 30px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(74, 222, 128, 0.3)',
                  transition: 'all 0.3s ease',
                  minWidth: '200px'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(74, 222, 128, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(74, 222, 128, 0.3)';
                }}
              >
                Go to Squad Tracker
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TalentPool;
